import html2pdf from 'html2pdf.js';
import { getNamespaceMessages } from '@/i18n';
import { intlLocale, getSavedLocale } from '@/i18n/locale';
import logoUrl from '@/assets/logo.png';

const BRAND = {
    purple: '#250891',
    purpleDark: '#160266',
    cyan: '#1ac4cd',
    cyanSoft: '#e8f8f9',
    purpleSoft: 'rgba(37, 8, 145, 0.08)',
    text: '#1a1d26',
    muted: '#5c6370',
    border: '#e0e4ef',
    white: '#ffffff',
    rowAlt: '#f7f8fc',
};

function escapeHtml(value) {
    return String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;')
        .replace(/—/g, '-');
}

function waitForPaint() {
    return new Promise((resolve) => {
        requestAnimationFrame(() => {
            requestAnimationFrame(resolve);
        });
    });
}

async function getLogoDataUrl() {
    try {
        const response = await fetch(logoUrl);
        const blob = await response.blob();
        return await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
        });
    } catch (e) {
        console.warn('Nao foi possivel carregar o logo Weeduc para o PDF.', e);
        return null;
    }
}

function slugify(value) {
    return String(value || '')
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, 40) || 'colaborador';
}

function formatDate(value, locale = intlLocale(getSavedLocale())) {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);
    return date.toLocaleDateString(locale);
}

function formatDateTime(value, locale = intlLocale(getSavedLocale())) {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);
    return date.toLocaleString(locale, {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
}

function statusLabel(status, t) {
    const map = {
        completed: t.detail.statusCompleted,
        in_progress: t.detail.statusInProgress,
        overdue: t.detail.statusOverdue,
        not_started: t.detail.statusNotStarted,
        active: t.collaborators.active,
        inactive: t.collaborators.inactive,
    };
    return map[status] || status || '-';
}

function sourceLabel(source, t) {
    const map = {
        role: t.collaborators.role,
        sector: t.collaborators.sector,
        group: t.coursesPage.group,
        manual: t.detail.sourceManual,
        user: t.detail.sourceManual,
    };
    return map[source] || source || '-';
}

function eventLabel(event, t) {
    const map = {
        organization_updated: t.detail.eventOrgUpdated,
        course_completed: t.detail.eventCourseCompleted,
        user_enrolled: t.detail.eventEnrolled,
    };
    return map[event?.type] || event?.type || '-';
}

function buildFilename(user) {
    const code = user?.internalCode || user?.id || 'id';
    const name = slugify(user?.name);
    return `colaborador-${code}-${name}.pdf`;
}

function sectionTitle(text) {
    return `
        <h2 style="
            margin: 0 0 10px;
            padding: 0 0 0 10px;
            font-size: 13px;
            font-weight: 700;
            color: ${BRAND.purple};
            border-left: 4px solid ${BRAND.cyan};
            line-height: 1.2;
            text-align: left;
        ">${escapeHtml(text)}</h2>
    `;
}

function thCell(text) {
    return `
        <th style="
            padding: 8px;
            border: 1px solid ${BRAND.border};
            background: ${BRAND.purple};
            color: ${BRAND.white};
            text-align: left;
            font-size: 11px;
            font-weight: 700;
        ">${escapeHtml(text)}</th>
    `;
}

function labelCell(text) {
    return `
        <td style="
            padding: 7px 8px;
            border: 1px solid ${BRAND.border};
            width: 30%;
            background: ${BRAND.purpleSoft};
            color: ${BRAND.purpleDark};
            font-weight: 700;
            font-size: 11px;
        ">${escapeHtml(text)}</td>
    `;
}

function valueCell(text) {
    return `
        <td style="
            padding: 7px 8px;
            border: 1px solid ${BRAND.border};
            color: ${BRAND.text};
            font-size: 12px;
        ">${escapeHtml(text)}</td>
    `;
}

export function buildCollaboratorReportHtml({
    user = {},
    progress = {},
    timeline = [],
    labels = getNamespaceMessages('corporate'),
    logoDataUrl = null,
} = {}) {
    const t = labels;
    const pdf = t.exportPdf || {};
    const locale = intlLocale(getSavedLocale());
    const enrolments = progress.enrolments || user.enrolments || [];
    const events = Array.isArray(timeline) ? timeline.slice(0, 12) : [];
    const generatedAt = new Date().toLocaleString(locale);

    const logoBlock = logoDataUrl
        ? `<img src="${logoDataUrl}" alt="Weeduc" style="height: 36px; width: auto; display: block;" />`
        : `<div style="font-size: 22px; font-weight: 800; color: ${BRAND.white}; letter-spacing: -0.02em;">weeduc</div>`;

    const courseRows = enrolments.length
        ? enrolments
            .map(
                (course, index) => `
                <tr style="background: ${index % 2 ? BRAND.rowAlt : BRAND.white};">
                    <td style="padding: 7px 8px; border: 1px solid ${BRAND.border}; color: ${BRAND.text};">${escapeHtml(course.courseName || '-')}</td>
                    <td style="padding: 7px 8px; border: 1px solid ${BRAND.border};">
                        <span style="
                            display: inline-block;
                            padding: 2px 8px;
                            border-radius: 999px;
                            font-size: 10px;
                            font-weight: 700;
                            background: ${course.mandatory ? BRAND.purpleSoft : '#eef1f6'};
                            color: ${course.mandatory ? BRAND.purple : BRAND.muted};
                        ">${escapeHtml(
                            course.mandatory
                                ? t.coursesPage.mandatory
                                : t.coursesPage.optional,
                        )}</span>
                    </td>
                    <td style="padding: 7px 8px; border: 1px solid ${BRAND.border}; color: ${BRAND.text};">${escapeHtml(statusLabel(course.status, t))}</td>
                    <td style="padding: 7px 8px; border: 1px solid ${BRAND.border}; color: ${BRAND.purple}; font-weight: 700;">${escapeHtml(String(course.progress || 0))}%</td>
                    <td style="padding: 7px 8px; border: 1px solid ${BRAND.border}; color: ${BRAND.muted};">${escapeHtml(sourceLabel(course.source, t))}</td>
                </tr>`,
            )
            .join('')
        : `<tr><td colspan="5" style="padding: 12px 8px; border: 1px solid ${BRAND.border}; color: ${BRAND.muted}; text-align: center;">${escapeHtml(t.detail.coursesEmpty)}</td></tr>`;

    const timelineRows = events.length
        ? events
            .map(
                (event, index) => `
                <tr style="background: ${index % 2 ? BRAND.rowAlt : BRAND.white};">
                    <td style="padding: 7px 8px; border: 1px solid ${BRAND.border}; color: ${BRAND.purple}; font-weight: 700;">${escapeHtml(eventLabel(event, t))}</td>
                    <td style="padding: 7px 8px; border: 1px solid ${BRAND.border}; color: ${BRAND.text};">${escapeHtml(event.description || '-')}</td>
                    <td style="padding: 7px 8px; border: 1px solid ${BRAND.border}; color: ${BRAND.muted};">${escapeHtml(formatDateTime(event.createdAt, locale))}</td>
                </tr>`,
            )
            .join('')
        : `<tr><td colspan="3" style="padding: 12px 8px; border: 1px solid ${BRAND.border}; color: ${BRAND.muted}; text-align: center;">${escapeHtml(t.detail.timelineEmpty)}</td></tr>`;

    const metricCard = (label, value) => `
        <td style="
            width: 20%;
            padding: 12px 8px;
            border: 1px solid ${BRAND.border};
            background: linear-gradient(180deg, ${BRAND.cyanSoft} 0%, ${BRAND.white} 100%);
            text-align: center;
        ">
            <div style="font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em; color: ${BRAND.muted}; margin-bottom: 4px;">
                ${escapeHtml(label)}
            </div>
            <div style="font-size: 18px; font-weight: 800; color: ${BRAND.purple};">
                ${escapeHtml(String(value))}
            </div>
        </td>
    `;

    return `
        <div id="collaborator-pdf-report" style="
            box-sizing: border-box;
            font-family: Arial, Helvetica, sans-serif;
            color: ${BRAND.text};
            text-align: left;
            width: 700px;
            padding: 0;
            margin: 0;
            background: ${BRAND.white};
        ">
            <div style="
                background: linear-gradient(135deg, ${BRAND.purpleDark} 0%, ${BRAND.purple} 55%, ${BRAND.cyan} 160%);
                border-radius: 12px;
                padding: 16px 18px;
                margin-bottom: 16px;
                color: ${BRAND.white};
            ">
                <div style="
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 16px;
                    margin-bottom: 14px;
                ">
                    <div style="
                        background: ${BRAND.white};
                        border-radius: 10px;
                        padding: 8px 12px;
                        display: inline-flex;
                        align-items: center;
                    ">
                        ${logoBlock}
                    </div>
                    <div style="text-align: right;">
                        <div style="font-size: 11px; opacity: 0.9; text-transform: uppercase; letter-spacing: 0.06em;">
                            ${escapeHtml(pdf.reportTitle || 'Relatorio corporativo')}
                        </div>
                        <div style="font-size: 12px; opacity: 0.85; margin-top: 2px;">
                            ${escapeHtml(pdf.generatedAt || 'Gerado em')} ${escapeHtml(generatedAt)}
                        </div>
                    </div>
                </div>
                <h1 style="margin: 0 0 6px; font-size: 24px; line-height: 1.2; color: ${BRAND.white};">
                    ${escapeHtml(user.name || t.detail.title)}
                </h1>
                <p style="margin: 0; font-size: 12px; color: rgba(255,255,255,0.9);">
                    ${escapeHtml(user.email || '-')}
                    &nbsp;|&nbsp;
                    ${escapeHtml(user.sectorName || pdf.noSector || 'Sem setor')}
                    &nbsp;|&nbsp;
                    ${escapeHtml(user.roleName || pdf.noRole || 'Sem cargo')}
                </p>
            </div>

            <section style="margin-bottom: 16px;">
                ${sectionTitle(pdf.summary || 'Resumo')}
                <table style="width: 100%; border-collapse: collapse;">
                    <tbody>
                        <tr>
                            ${labelCell(t.collaborators.status)}
                            ${valueCell(statusLabel(user.status, t))}
                            ${labelCell(t.detail.overallProgress)}
                            ${valueCell(`${user.progressPercent || 0}%`)}
                        </tr>
                        <tr>
                            ${labelCell(t.collaborators.sector)}
                            ${valueCell(user.sectorName || '-')}
                            ${labelCell(t.collaborators.role)}
                            ${valueCell(user.roleName || '-')}
                        </tr>
                        <tr>
                            ${labelCell(t.collaborators.manager)}
                            ${valueCell(user.managerName || '-')}
                            ${labelCell(t.collaborators.internalCode)}
                            ${valueCell(user.internalCode || '-')}
                        </tr>
                    </tbody>
                </table>
            </section>

            <section style="margin-bottom: 16px;">
                ${sectionTitle(t.detail.eduSummary)}
                <table style="width: 100%; border-collapse: collapse;">
                    <tbody>
                        <tr>
                            ${metricCard(t.detail.courses, progress.totalAssigned || enrolments.length || 0)}
                            ${metricCard(t.detail.completed, progress.completed || 0)}
                            ${metricCard(t.detail.overdue, progress.overdue || 0)}
                            ${metricCard(t.detail.points, progress.gamificationPoints || user.gamificationPoints || 0)}
                            ${metricCard(t.detail.level, progress.gamificationLevel || user.gamificationLevel || '-')}
                        </tr>
                    </tbody>
                </table>
            </section>

            <section style="margin-bottom: 16px;">
                ${sectionTitle(t.detail.orgData)}
                <table style="width: 100%; border-collapse: collapse;">
                    <tbody>
                        <tr>
                            ${labelCell(t.collaborators.name)}
                            ${valueCell(user.name || '-')}
                        </tr>
                        <tr>
                            ${labelCell(t.collaborators.email)}
                            ${valueCell(user.email || '-')}
                        </tr>
                        <tr>
                            ${labelCell(t.collaborators.admissionDate)}
                            ${valueCell(formatDate(user.admissionDate, locale))}
                        </tr>
                        <tr>
                            ${labelCell(t.collaborators.responsible)}
                            ${valueCell(user.isResponsible ? t.users.yes : t.users.no)}
                        </tr>
                    </tbody>
                </table>
            </section>

            <section style="margin-bottom: 16px;">
                ${sectionTitle(t.detail.courseHistory)}
                <table style="width: 100%; border-collapse: collapse; font-size: 11px;">
                    <thead>
                        <tr>
                            ${thCell(pdf.course || 'Curso')}
                            ${thCell(pdf.type || 'Tipo')}
                            ${thCell(t.collaborators.status)}
                            ${thCell(t.collaborators.progress)}
                            ${thCell(t.detail.source)}
                        </tr>
                    </thead>
                    <tbody>
                        ${courseRows}
                    </tbody>
                </table>
            </section>

            <section style="margin-bottom: 14px;">
                ${sectionTitle(t.detail.timeline)}
                <table style="width: 100%; border-collapse: collapse; font-size: 11px;">
                    <thead>
                        <tr>
                            ${thCell(pdf.event || 'Evento')}
                            ${thCell(pdf.description || 'Descricao')}
                            ${thCell(pdf.date || 'Data')}
                        </tr>
                    </thead>
                    <tbody>
                        ${timelineRows}
                    </tbody>
                </table>
            </section>

            <footer style="
                margin-top: 8px;
                padding-top: 12px;
                border-top: 2px solid ${BRAND.cyan};
                display: flex;
                align-items: center;
                justify-content: space-between;
                gap: 12px;
            ">
                <div style="display: flex; align-items: center; gap: 8px;">
                    ${
                        logoDataUrl
                            ? `<img src="${logoDataUrl}" alt="Weeduc" style="height: 22px; width: auto; display: block;" />`
                            : `<strong style="color: ${BRAND.purple};">weeduc</strong>`
                    }
                    <span style="font-size: 10px; color: ${BRAND.muted};">
                        ${escapeHtml(pdf.platformTagline || 'Plataforma de educacao corporativa')}
                    </span>
                </div>
                <span style="font-size: 10px; color: ${BRAND.purple}; font-weight: 700;">
                    www.weeduc.com.br
                </span>
            </footer>
        </div>
    `;
}

export async function exportCollaboratorPdf({
    user,
    progress = {},
    timeline = [],
    labels = getNamespaceMessages('corporate'),
} = {}) {
    if (!user) {
        const pdf = labels?.exportPdf || getNamespaceMessages('corporate').exportPdf || {};
        throw new Error(pdf.notFound || 'Colaborador nao encontrado para exportacao.');
    }

    const logoDataUrl = await getLogoDataUrl();
    const html = buildCollaboratorReportHtml({
        user,
        progress,
        timeline,
        labels,
        logoDataUrl,
    });

    const host = document.createElement('div');
    host.setAttribute('aria-hidden', 'true');
    host.style.cssText = [
        'position: fixed',
        'left: 0',
        'top: 0',
        'width: 740px',
        'padding: 20px',
        'background: #ffffff',
        'opacity: 0.01',
        'pointer-events: none',
        'z-index: 2147483647',
    ].join(';');
    host.innerHTML = html;
    document.body.appendChild(host);

    const report = host.querySelector('#collaborator-pdf-report') || host;
    const logoImages = Array.from(report.querySelectorAll('img'));
    await Promise.all(
        logoImages.map(
            (img) =>
                new Promise((resolve) => {
                    if (img.complete) {
                        resolve();
                        return;
                    }
                    img.onload = () => resolve();
                    img.onerror = () => resolve();
                }),
        ),
    );
    await waitForPaint();

    const opt = {
        margin: [10, 10, 10, 10],
        filename: buildFilename(user),
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: {
            scale: 2,
            useCORS: true,
            logging: false,
            backgroundColor: '#ffffff',
            scrollX: 0,
            scrollY: -window.scrollY,
            windowWidth: Math.max(report.scrollWidth, 740),
            windowHeight: Math.max(report.scrollHeight, 400),
            onclone(clonedDoc) {
                const clonedHost = clonedDoc.body.querySelector('[aria-hidden="true"]');
                if (clonedHost) {
                    clonedHost.style.opacity = '1';
                }
                const clonedReport = clonedDoc.getElementById('collaborator-pdf-report');
                if (clonedReport) {
                    clonedReport.style.opacity = '1';
                    clonedReport.style.color = BRAND.text;
                    clonedReport.style.background = BRAND.white;
                }
            },
        },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
    };

    try {
        await html2pdf().set(opt).from(report).save();
    } finally {
        host.remove();
    }
}

export default {
    buildCollaboratorReportHtml,
    exportCollaboratorPdf,
};
